import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import clsx from 'clsx';

import { Dicon, Button, Image } from 'edcom-ui';
import content from '../../../services/content';
import memberService from '../../../services/memberApi';

import AccountContext from '../../../services/AccountContext';
import { CollectionsProvider } from '../../../services/CollectionsContext';
import { openSignup } from '../../../services/regModal';

import Head from '../../../components/head';
import Breadcrumbs from '../../../components/Breadcrumbs';
import CategoryTree from '../../../components/CategoryTree/CategoryTree';
import RelatedGuidedLesson from '../../../components/RelatedGuidedLessons';

import styles from './worksheet.module.less';
import { openPayWall } from '../../../services/payWallModal';
import Credits from '../../../components/Credits';
import WorksheetActions from '../../../components/worksheet/WorksheetActions';
import Article from '../../../components/StructuredData/Article';
import track, { trackGA } from '../../../services/track';

import Error, { getPageNotFoundTitle } from '../../../components/Error';
import AdminLinks from '../../../components/AdminLinks/AdminLinks';

const RelatedLearningResources = dynamic(
  () => import('../../../components/RelatedLearningResources'),
  { ssr: false }
);

const ShareOptions = dynamic(() => import('../../../components/ShareOptions'), {
  ssr: false,
});

const Worksheet = ({
  id,
  title,
  urlName,
  metaTitle = '',
  metaDescription = '',
  published = false,
  indexed = false,
  isInteractive = false,
  text = '',
  updatedAt,
  createdAt,
  standards = [],
  grades = [],
  subjects = [],
  mainImage = null,
  lessonPlan = null,
  guidedLesson = null,
  slideshow = null,
  downloadHref,
  answersheetHref = null,
  error = false,
}) => {
  const { ready, memberId, isPremium, isAdmin } = useContext(AccountContext);

  const [refreshCredits, setRefreshCredits] = useState(false);
  const [canDownload, setCanDownload] = useState(false);

  const hideUnpublished = !published && !isAdmin;

  if (
    hideUnpublished ||
    (error && error.statusCode && error.statusCode === 404)
  ) {
    if (!error) error = { statusCode: 404 }; // Return a 404 (Not Found) for non-admins if the content is unpublished
    metaTitle = getPageNotFoundTitle();
  }

  let downloadPageButtonText = 'Print Worksheet';
  if (ready && !memberId) {
    downloadPageButtonText = 'Download Free Worksheet';
  }

  // Sort by length of internalName. This puts the more general subjects before more specific ones
  subjects.sort((a, b) => a.internalName.length - b.internalName.length);

  const relatedGuidedLesson =
    guidedLesson && guidedLesson.contentCounts !== undefined ? (
      <RelatedGuidedLesson {...guidedLesson} />
    ) : null;

  const trackDownload = ({
    button = null,
    googleDrive = false,
    answerKey = false,
  }) => {
    track('Downloaded Content', {
      contentId: id,
      contentType: answerKey ? 'worksheet-answerkey' : 'worksheet',
      name: title,
      section: 'Worksheet Detail',
      button,
      method: googleDrive ? 'google-drive' : '',
    });
    // Hacky way of having a different eventAction for the Open Interactive button
    button === 'open-interactive-button'
      ? trackGA('worksheet', 'open-interactive-button')
      : trackGA('worksheet', answerKey ? 'download answerkey' : 'download');
  };

  useEffect(() => {
    memberService.checkCanDownload('worksheet', id).then(resp => {
      setCanDownload(resp?.allowed === true);
    });
  }, [memberId, isPremium]);

  const handleImageClick = async event => {
    // Same as the main download button, but different button name for analytics tracking
    isInteractive
      ? openInteractiveWorksheet(event)
      : await handleDownloadPageAction(event, 'image_hover');
  };

  const handlePaywallActions = async (event, button, action) => {
    if (!memberId) {
      event.preventDefault();
      openSignup('download', {
        signUp: { title: 'Join Education.com for Free Access to Worksheets' },
      });
      return;
    }

    if (!isPremium) {
      event.preventDefault();
      if (canDownload === true) {
        trackDownload({ button });
        setRefreshCredits(!refreshCredits);
        action();
      } else {
        openPayWall({
          contentId: id,
          contentType: 'worksheet',
          section: 'Worksheet Detail',
          feature: 'Worksheet Download',
          name: title,
          cid: '10.851',
        });
      }
    } else {
      trackDownload({ button });
      action();
    }
  };

  const handleDownloadPageAction = async (event, button = 'main') => {
    event.preventDefault();
    const action = () => {
      window.open(downloadHref, '_blank', 'noopener');
    };
    await handlePaywallActions(event, button, action);
  };

  const openInteractiveWorksheet = async (
    event,
    button = 'open-interactive-button'
  ) => {
    event.preventDefault();
    const action = () => {
      window.open(`/open-interactive/worksheet/${urlName}/`, '_self');
    };
    await handlePaywallActions(event, button, action);
  };

  // Record page view for popularity tracking
  useEffect(() => {
    content().recordView('worksheet', id);
  }, [id]);

  // SEO test to add grade/subject to the content type for 5th grade only
  let contentTypeDisplay = 'Worksheet';
  if (grades[0] && grades[0].urlName === 'fifth-grade') {
    if (subjects[0]) {
      // Get the 2nd level subject (e.g. if subjects[0] is math, this will match math.fractions)
      const regex = new RegExp(
        '^' + subjects[0].internalName.replace(/\./g, '\\.') + '\\.[^.]+$'
      );
      const match = subjects.filter(s => s.internalName.match(regex))[0];
      if (match) {
        contentTypeDisplay = match.name + ' ' + contentTypeDisplay;
      }
    }
    contentTypeDisplay = '5th Grade ' + contentTypeDisplay;
  }

  const iconSvgAnimated = (
    <img src="/files/static/interactive-worksheets/icons/sparkles-icon-white.svg" />
  );

  const iconSvgStatic = (
    <svg
      className={styles.iconStaticSvg}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="4 1 20 26"
      fill="white"
    >
      <path
        d="M4.107,7.962c1.844-0.157,3.164-1.025,4.255-2.217c1.389-1.519,2.198-3.292,2.483-5.271  C10.868,0.317,10.904,0.163,10.936,0c0.235,2.273,1.115,4.284,2.761,5.973c1.053,1.081,2.319,1.841,4.041,1.988  c-0.502,0.112-0.909,0.169-1.291,0.293c-1.553,0.503-2.695,1.494-3.601,2.735c-1.1,1.506-1.752,3.166-1.914,4.993  c-0.164-1.528-0.652-2.962-1.468-4.291C8.525,10.164,7.292,8.914,5.471,8.28C5.066,8.139,4.628,8.081,4.107,7.962z"
        fill="white"
      />
      <path
        d="M21,20.865c-1.359,0.148-2.387,0.833-3.234,1.775c-1.096,1.219-1.74,2.747-1.9,4.359  c-0.489-3.747-3.017-5.987-4.956-6.101c0.533-0.229,1.289-0.437,1.898-0.838c1.58-1.041,2.416-2.559,2.857-4.28  c0.087-0.34,0.132-0.689,0.201-1.06c0.128,1.506,0.688,2.834,1.618,4.031C18.359,19.874,19.478,20.648,21,20.865z"
        fill="white"
      />
      <path
        d="M3.696,14.24c0.462,2.06,1.339,3.808,3.707,4.482c-2.352,0.688-3.244,2.424-3.707,4.499  C3.257,21.142,2.342,19.43,0,18.723C2.35,18.047,3.244,16.319,3.696,14.24z"
        fill="white"
      />
    </svg>
  );

  return (
    <>
      <Head
        description={metaDescription}
        index={indexed}
        image={mainImage}
        title={metaTitle}
      >
        <title>{metaTitle}</title>
        <meta property="og:type" content="educationdotcom:worksheet" />
        <Article
          createdAt={createdAt ? createdAt.replace(/\s+/, 'T') : ''}
          updatedAt={updatedAt ? updatedAt.replace(/\s+/, 'T') : null}
          path={`/worksheet/article/${urlName}/`}
          image={mainImage}
          title={title}
          description={metaDescription}
          contentType="worksheet"
        />
      </Head>
      <div
        className={clsx('page-width', 'typography-small', styles.detailContent)}
      >
        {error ? (
          <Error err={error} />
        ) : (
          <CollectionsProvider>
            <Breadcrumbs
              className={styles.breadcrumbs}
              title={title}
              contentType="Worksheets"
              grades={grades}
              subjects={subjects}
              path="worksheets"
            />
            <div>
              <div>
                <h3 className={styles.contentTitle}>
                  <Dicon name="worksheet" theme="dark" />{' '}
                  <span className={styles.contentName}>
                    {contentTypeDisplay}
                  </span>
                </h3>
                <h1>{title}</h1>
                {!!memberId && (
                  <ShareOptions
                    name={title}
                    imageSrc={mainImage}
                    contentType={'worksheet'}
                    contentId={id}
                    downloadHref={downloadHref}
                    canDownload={canDownload}
                  />
                )}
              </div>
              <div className={styles.content}>
                <div className={styles.thumbnailContainer}>
                  {isInteractive && (
                    <div className={styles.bannerContainer}>
                      <div className={styles.interactiveBanner}>
                        {iconSvgStatic}
                      </div>
                    </div>
                  )}
                  <div className={styles.thumbnail}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleImageClick}
                    >
                      <Image src={mainImage} alt={title} lazy={false} />
                    </a>
                  </div>
                </div>
                <div className={styles.contentDetailContainer}>
                  <div dangerouslySetInnerHTML={{ __html: text }} />
                  <Credits refreshCredits={refreshCredits} />
                  <div className={styles.mainActionButtons}>
                    <div className={styles.actionButtonContainer}>
                      <Button
                        size="normal"
                        onClick={handleDownloadPageAction}
                        target="_blank"
                        className={styles.downloadActionButton}
                        color={isInteractive ? 'secondary' : 'primary'}
                      >
                        {downloadPageButtonText}
                      </Button>
                    </div>
                    {isInteractive && (
                      <div className={styles.actionButtonContainer}>
                        <Button
                          size="normal"
                          className={styles.interactiveActionButton}
                          onClick={openInteractiveWorksheet}
                        >
                          {iconSvgAnimated}
                          Open Interactive Worksheet
                        </Button>
                      </div>
                    )}
                  </div>
                  <WorksheetActions
                    id={id}
                    title={title}
                    thumbnail={mainImage}
                    lessonPlan={lessonPlan}
                    slideshow={slideshow}
                    answersheetHref={answersheetHref}
                    trackDownload={trackDownload}
                    downloadHref={downloadHref}
                    isInteractive={isInteractive}
                  />
                  <CategoryTree
                    resource={{
                      grades: grades,
                      subjects: subjects,
                      standards: standards,
                    }}
                  />
                </div>
              </div>
              {relatedGuidedLesson}
              <RelatedLearningResources type="worksheet" id={id} />
            </div>
          </CollectionsProvider>
        )}
      </div>
      <AdminLinks>
        <a
          href={`/secure/content/editor.php?nocache&type=worksheet&id=${id}`}
          target="_blank"
        >
          Edit Worksheet
        </a>
        {isInteractive && (
          <a
            href={`/secure/fillableWorksheet/editor.php?nocache&id=${id}`}
            target="_blank"
          >
            Edit Interactive
          </a>
        )}
      </AdminLinks>
    </>
  );
};

Worksheet.getInitialProps = async ({ asPath, req, query, res }) => {
  const contentService = content(req);

  const { urlname } = query;
  try {
    const response = await contentService.getWorksheetDetails(urlname);

    // Return a 404 (Not Found) for unpublished resources
    if (!response.published) res.statusCode = 404;

    response.pageKey = asPath;

    // Footer props for Tracking
    response.footerProps = {
      grades: response?.grades
        ? response.grades.map(grade => grade.urlName)
        : [],
      subjects: response?.subjects
        ? response.subjects.map(subject => subject.internalName)
        : [],
      contentType: 'worksheet',
      pageType: 'Content',
      isInteractive: response?.isInteractive,
    };

    return response;
  } catch (e) {
    if (e.statusCode === 404) {
      res.statusCode = e.statusCode;
    }
    return {
      error: e,
      errorDescription: e.message,
    };
  }
};

const categoryShape = PropTypes.shape({
  urlName: PropTypes.string,
  internalName: PropTypes.string,
  name: PropTypes.string,
});

Worksheet.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  metaTitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  published: PropTypes.bool,
  indexed: PropTypes.bool,
  isInteractive: PropTypes.bool,
  text: PropTypes.string.isRequired,
  updatedAt: PropTypes.string,
  createdAt: PropTypes.string,
  standards: PropTypes.arrayOf(categoryShape),
  grades: PropTypes.arrayOf(categoryShape),
  subjects: PropTypes.arrayOf(categoryShape),
  mainImage: PropTypes.string,
  lessonPlan: PropTypes.string,
  guidedLesson: PropTypes.object,
  slideshow: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    count: PropTypes.number,
  }),
  downloadHref: PropTypes.string.isRequired,
  answersheetHref: PropTypes.string,
  error: PropTypes.bool,
};

export default Worksheet;
